import type { ConditionRule } from "./types";

/**
 * Условия аудитории по признакам гостя (пакет 4 «50 улучшений SMS-центра»):
 * сегмент компании, просрочка ритма, ожидаемый заказ, риск ухода.
 * Признаки считаются ночью по заказам КОМПАНИИ (go/retention/guest_features.go),
 * поэтому у гостя без истории значения нет и правило для него не выполняется.
 * Здесь — справочник полей, подписи простым языком и проверка значений; рисует
 * поля `GuestFeatureControls.tsx`.
 */

export type GuestFeatureKind =
  | "segments"
  | "ratio"
  | "days"
  | "percent"
  | "minutes";
export type GuestFeatureOp = ConditionRule["op"] | "in";

export interface GuestFeatureField {
  label: string;
  kind: GuestFeatureKind;
  /** Единица в подписи числа («раз», «дн.», «%»). */
  unit?: string;
  /** Какие сравнения доступны (как их принимает Go). */
  ops: GuestFeatureOp[];
  step?: number;
  max?: number;
  /** Пояснение под правилом простым языком. */
  hint: string;
}

/** Сегменты — коды Go (rfmSegmentCodes), порядок как в справочнике RFM. */
export const GUEST_SEGMENTS: ReadonlyArray<{ code: string; title: string }> = [
  { code: "champions", title: "Чемпионы" },
  { code: "loyal", title: "Лояльные" },
  { code: "lagging", title: "Отстающие" },
  { code: "regular", title: "Постоянные" },
  { code: "casual", title: "Случайные" },
  { code: "sleeping", title: "Спящие" },
  { code: "newbie", title: "Новички" },
  { code: "lowvalue", title: "Неценные" },
  { code: "lost", title: "Потерянные" },
];

export const GUEST_FEATURE_FIELDS: Record<string, GuestFeatureField> = {
  rfmSegment: {
    label: "Сегмент гостя",
    kind: "segments",
    ops: ["in"],
    hint: "Сегмент считается по заказам выбранных точек на сегодня — так же, как на экране «Сегменты гостей». Выберите один или несколько.",
  },
  overdueRatio: {
    label: "Просрочил свой ритм (во сколько раз)",
    kind: "ratio",
    unit: "раз",
    ops: ["gte", "lte"],
    step: 0.1,
    max: 1000,
    hint: "У каждого гостя свой ритм — как часто он обычно заказывает. 1 — пора заказывать, 2 — прошло вдвое больше обычного. Ритм известен у гостей, которые за год заказывали в три и более разных дня; остальные в это условие не попадают.",
  },
  expectedNextInDays: {
    label: "Ожидаем заказ в ближайшие дни",
    kind: "days",
    unit: "дн.",
    ops: ["lte"],
    max: 365,
    hint: "Гости, у которых по их обычному ритму следующий заказ выпадает на ближайшие дни. Те, кто уже просрочил ритм, сюда не входят — для них есть условие «Просрочил свой ритм».",
  },
  lowRatingMinutes: {
    label: "С плохой оценки заказа прошло",
    kind: "minutes",
    unit: "мин.",
    ops: ["gte", "lte"],
    step: 1,
    max: 10080,
    hint: "Гость поставил заказу 1–3 звезды в приложении или на сайте; берём его последнюю оценку на точках кампании. «Не меньше 60» — через час после оценки, «не больше 2880» — не позже двух суток. Если после этого гость поставил 4–5 звёзд, условие не выполняется. Оценки карт и агрегаторов к гостю не привязаны.",
  },
  churnRisk: {
    label: "Риск ухода",
    kind: "percent",
    unit: "%",
    ops: ["gte", "lte"],
    max: 100,
    hint: "Оценка по истории вашей компании: насколько вероятно, что гость не закажет в ближайшие 30 дней. Появляется, когда у компании накопилось не меньше 200 гостей с историей; пересчитывается ночью.",
  },
};

export const isGuestFeatureField = (field: string): boolean =>
  Object.prototype.hasOwnProperty.call(GUEST_FEATURE_FIELDS, field);

export const guestSegmentTitle = (code: string): string =>
  GUEST_SEGMENTS.find((s) => s.code === code)?.title ?? code;

/** Выбранные сегменты правила (только известные коды, без повторов). */
export function ruleSegments(rule: ConditionRule): string[] {
  const value: unknown = rule.value;
  if (!Array.isArray(value)) return [];
  const chosen = new Set(
    value.filter((v): v is string => typeof v === "string"),
  );
  return GUEST_SEGMENTS.map((s) => s.code).filter((code) => chosen.has(code));
}

/** Новое правило при выборе поля: безопасные значения по умолчанию. */
export function newGuestFeatureRule(field: string): ConditionRule {
  switch (field) {
    case "rfmSegment":
      return { kind: "rule", field, op: "in", value: ["sleeping"] };
    case "overdueRatio":
      return { kind: "rule", field, op: "gte", value: 1.5 };
    case "expectedNextInDays":
      return { kind: "rule", field, op: "lte", value: 3 };
    case "lowRatingMinutes":
      return { kind: "rule", field, op: "gte", value: 60 };
    default:
      return { kind: "rule", field, op: "gte", value: 70 };
  }
}

/** Включить/выключить сегмент; порядок — как в справочнике. */
export function toggleRuleSegment(
  rule: ConditionRule,
  code: string,
): ConditionRule {
  const current = new Set(ruleSegments(rule));
  if (current.has(code)) current.delete(code);
  else current.add(code);
  return {
    ...rule,
    op: "in",
    value: GUEST_SEGMENTS.map((s) => s.code).filter((c) => current.has(c)),
  };
}

const OP_WORDS: Record<string, string> = {
  gte: "не меньше",
  lte: "не больше",
  eq: "ровно",
};

/** «в 1 раз», «в 2 раза», «в 1,5 раза», «в 5 раз». */
export function timesWord(value: number): string {
  if (!Number.isInteger(value)) return "раза";
  const last = value % 10;
  const tens = value % 100;
  if (tens >= 11 && tens <= 14) return "раз";
  if (last === 1) return "раз";
  if (last >= 2 && last <= 4) return "раза";
  return "раз";
}

function numberText(value: ConditionRule["value"]): string {
  return String(value).replace(".", ",");
}

/** Строка правила для «Как система прочитает правило». */
export function guestFeatureLabel(rule: ConditionRule): string {
  const field = GUEST_FEATURE_FIELDS[rule.field];
  if (!field) return "Неизвестное условие";
  if (field.kind === "segments") {
    const titles = ruleSegments(rule).map(guestSegmentTitle);
    return `${field.label}: ${titles.length ? titles.join(" или ") : "не выбран"}`;
  }
  if (rule.field === "expectedNextInDays")
    return `Ожидаем заказ в ближайшие ${numberText(rule.value)} дн.`;
  if (rule.field === "lowRatingMinutes")
    return `С плохой оценки заказа прошло ${OP_WORDS[rule.op] ?? ""} ${numberText(rule.value)} мин.`;
  if (rule.field === "overdueRatio") {
    const word =
      rule.op === "eq" ? "ровно в" : `${OP_WORDS[rule.op] ?? ""} чем в`;
    return `Просрочил свой ритм ${word} ${numberText(rule.value)} ${timesWord(Number(rule.value))}`;
  }
  return `${field.label} ${OP_WORDS[rule.op] ?? ""} ${numberText(rule.value)} ${field.unit ?? ""}`.trim();
}

/** Ошибки правила простыми словами; пустой список — правило верное. */
export function guestFeatureRuleErrors(rule: ConditionRule): string[] {
  const field = GUEST_FEATURE_FIELDS[rule.field];
  if (!field) return ["Условие использует неизвестное поле."];
  if (field.kind === "segments") {
    if (rule.op !== "in" || !ruleSegments(rule).length)
      return [`${field.label}: выберите хотя бы один сегмент.`];
    return [];
  }
  if (!field.ops.includes(rule.op))
    return [`${field.label}: это сравнение недоступно.`];
  const raw: unknown = rule.value;
  if (raw === "" || typeof raw === "boolean" || Array.isArray(raw))
    return [`${field.label}: укажите число.`];
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0)
    return [`${field.label}: введите число не меньше нуля.`];
  if (field.max !== undefined && value > field.max)
    return [`${field.label}: не больше ${field.max}.`];
  if (field.kind === "days" && !Number.isInteger(value))
    return [`${field.label}: нужно целое число дней.`];
  if (field.kind === "minutes" && !Number.isInteger(value))
    return [`${field.label}: нужно целое число минут.`];
  return [];
}

/** Сегмент по числу заказов и давности — та же карта, что в Go (gfSegmentFor) и в `recomputeGuests`. */
export function segmentForOrders(orders: number, daysSince: number): string {
  if (orders >= 6) {
    if (daysSince <= 30) return "champions";
    return daysSince <= 90 ? "loyal" : "lagging";
  }
  if (orders >= 2) {
    if (daysSince <= 30) return "regular";
    return daysSince <= 90 ? "casual" : "sleeping";
  }
  if (daysSince <= 30) return "newbie";
  return daysSince <= 90 ? "lowvalue" : "lost";
}

/** Сегмент тестового гостя из его значений (ordersCount, daysSinceLastOrder). */
export function segmentOfFacts(
  facts: Record<string, number | boolean>,
): string {
  return segmentForOrders(
    Number(facts.ordersCount ?? 0),
    Number(facts.daysSinceLastOrder ?? 0),
  );
}

/** Значения тестового гостя, при которых он попадает в сегмент (для примера в симуляторе). */
export function exampleSegmentFacts(code: string): Record<string, number> {
  const orders = ["champions", "loyal", "lagging"].includes(code)
    ? 6
    : ["regular", "casual", "sleeping"].includes(code)
      ? 3
      : 1;
  const days = ["champions", "regular", "newbie"].includes(code)
    ? 10
    : ["loyal", "casual", "lowvalue"].includes(code)
      ? 60
      : 120;
  return {
    ordersCount: orders,
    daysSinceLastOrder: days,
    ordersLast30Days: days <= 30 ? Math.min(orders, 2) : 0,
  };
}
